'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { useTranslations } from 'next-intl';
import LanguageSwitcher from '@/components/LanguageSwitcher';
import { SignupCTA } from '../_shared';

/**
 * Navbar v2 — Stripe-style glass header.
 *
 * - 64px height, sticky with backdrop-blur on scroll
 * - Pill CTA matching landing primitives
 * - Subtle bottom-border instead of shadow
 * - Mobile: full-screen sheet with backdrop-blur
 */
export default function Navbar() {
  const t = useTranslations();
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Lock body scroll while mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
  }, [isMobileMenuOpen]);

  const navigation = [
    { name: t('landing.nav.features'), href: '/#features' },
    { name: t('landing.nav.industries'), href: '/branchen' },
    { name: t('landing.nav.pricing'), href: '/#pricing' },
    { name: t('landing.nav.testimonials'), href: '/#testimonials' },
    { name: t('landing.nav.faq'), href: '/#faq' },
    { name: t('landing.nav.blog'), href: '/blog' },
  ];

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${
          isScrolled
            ? 'border-b border-gray-200/60 bg-white/95 backdrop-blur-xl shadow-[0_1px_2px_rgba(15,23,42,0.04)]'
            : 'border-b border-transparent bg-white/0'
        }`}
      >
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            {/* Logo */}
            <Link
              href="/"
              className="flex flex-shrink-0 items-center focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 rounded-sm"
              aria-label="EazyBooking — zur Startseite"
            >
              {/* eslint-disable-next-line @next/next/no-img-element */}
              <img
                src="/logo.svg"
                alt="EazyBooking"
                className="h-9 w-auto"
              />
            </Link>

            {/* Desktop Nav */}
            <nav
              className="hidden lg:flex lg:items-center lg:gap-1"
              aria-label="Hauptnavigation"
            >
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="rounded-full px-3.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Desktop right-side */}
            <div className="hidden items-center gap-2 lg:flex">
              <LanguageSwitcher />
              <Link
                href="/admin/login"
                className="rounded-full px-3.5 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-100 hover:text-gray-950 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
              >
                {t('landing.nav.login')}
              </Link>
              <SignupCTA variant="primary" size="md" source="navbar">
                {t('landing.nav.getStarted')}
              </SignupCTA>
            </div>

            {/* Mobile right-side */}
            <div className="flex items-center gap-2 lg:hidden">
              <LanguageSwitcher />
              <button
                type="button"
                onClick={() => setIsMobileMenuOpen((v) => !v)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gray-200 bg-white/80 text-gray-700 backdrop-blur-sm transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                aria-label={isMobileMenuOpen ? t('landing.nav.menuClose') : t('landing.nav.menuOpen')}
                aria-expanded={isMobileMenuOpen}
              >
                {isMobileMenuOpen ? (
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                ) : (
                  <svg
                    className="h-5 w-5"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <line x1="3" y1="6" x2="21" y2="6" />
                    <line x1="3" y1="12" x2="21" y2="12" />
                    <line x1="3" y1="18" x2="21" y2="18" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile full-screen sheet — `inert` removes focusable descendants
          from the a11y tree + tab order when closed (replaces aria-hidden,
          which still allowed focus). */}
      <div
        className={`fixed inset-0 z-40 lg:hidden ${
          isMobileMenuOpen ? '' : 'pointer-events-none'
        }`}
        inert={!isMobileMenuOpen}
      >
        {/* Backdrop */}
        <div
          className={`absolute inset-0 bg-gray-950/30 backdrop-blur-sm transition-opacity duration-200 ${
            isMobileMenuOpen ? 'opacity-100' : 'opacity-0'
          }`}
          onClick={() => setIsMobileMenuOpen(false)}
        />
        {/* Sheet */}
        <div
          className={`absolute inset-x-0 top-16 bottom-0 overflow-y-auto bg-white transition-transform duration-300 ease-out ${
            isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'
          }`}
        >
          <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6">
            <nav className="flex flex-col gap-1" aria-label="Mobile Navigation">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className="rounded-xl px-4 py-3 text-base font-medium text-gray-950 transition-colors hover:bg-gray-100"
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.name}
                </Link>
              ))}
            </nav>
            <div className="mt-6 border-t border-gray-200 pt-6 space-y-3">
              <Link
                href="/admin/login"
                className="block rounded-xl px-4 py-3 text-base font-medium text-gray-950 transition-colors hover:bg-gray-100"
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {t('landing.nav.login')}
              </Link>
              <SignupCTA variant="primary" size="lg" fullWidth source="navbar_mobile">
                {t('landing.nav.getStarted')}
                <span aria-hidden="true">→</span>
              </SignupCTA>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
